import {makeObservable, observable, computed, action} from 'mobx';
import {size} from 'lodash';

import {ctrlHalfNodeHeight} from '../../cablingMapEditor/const';
import {NODE_ROLES, RAIL_ZONE, railGap, reZonesTitles} from '../const';
import Node from './Node';
import RackStore from './RackStore';
import {ZoneRoleType} from '../types';

class Zone {
  @observable label: string;
  @observable _size: number;
  @observable role: ZoneRoleType;
  @observable roleBased: boolean = true;
  @observable.ref topZone: Zone | null = null;
  @observable.ref rackStore: RackStore;

  @observable maxY: number;

  @computed
  get y(): number {
    return (this.topZone?.y ?? 0) + (this.topZone?.size ?? 0);
  }

  @computed
  get isRail(): boolean {
    return this.role === RAIL_ZONE;
  }

  @computed
  get isFirst(): boolean {
    return !this.topZone;
  }

  @computed
  get isLast(): boolean {
    return this.role === NODE_ROLES.GENERIC;
  }

  @action
  setSize(value: number) {
    this._size = value;
  }

  set size(value: number) {
    this.setSize(value);
  }

  @computed
  get size(): number {
    return this.roleBased ?
      this.isEmpty ? 30 : (this.isLast ? 1000 : this._size) :
      (1 + size(this.rackStore.usedRailIndices)) * railGap;
  }

  @computed
  get nodesCount(): number {
    return this.rackStore?.rolesCount[this.role] ?? 0;
  }

  @computed
  get isEmpty() {
    return this.roleBased && !this.nodesCount;
  }

  @action
  resizeBy(dy: number) {
    if (!this.roleBased) return;
    if (this.isEmpty) return this.topZone?.resizeBy(dy);
    if (this.size + dy >= +this.maxY) {
      this.size += dy;
    }
  }

  @action
  makeCustom() {
    this.roleBased = false;
  }

  @action
  adoptNode(node: Node): boolean {
    const y = ctrlHalfNodeHeight + (node.position?.y ?? 0);
    if (!this.isRail && y > this.y && y < this.y + this.size) {
      node.zone = this;
      node.role = this.role as NODE_ROLES;
      node.moveTo({x: node.position.x, y: y - this.y - ctrlHalfNodeHeight});
      return true;
    }
    return false;
  }

  constructor(role: ZoneRoleType, topZone: Zone | null, rackStore: RackStore) {
    makeObservable(this);

    this.label = reZonesTitles[role];
    this.role = role;
    this._size = 150;
    this.topZone = topZone;
    this.rackStore = rackStore;

    this.maxY = this.size;
  }
}

export default Zone;
